/**
 * What a speaker label looks like, and when two labels name the same person.
 * Every transcript reader shares these rules, so a name that opens a turn in one
 * view opens it in all of them.
 *
 * The label tables are not semantic keyword matching. They are the exact
 * prefixes this codebase's own transcript writers emit: `conversationFromChat`
 * ("Customer" / the session user's name), the phone bridge ("Agent" / "User")
 * and the on-the-fly runner ("Salesperson" / "You"), i.e. enum values.
 */

/** Prefixes our transcript writers use for the learner. */
export const REP_SPEAKER_LABELS = new Set([
  "you",
  "user",
  "rep",
  "sales rep",
  "salesperson",
  "sales person",
  "seller",
]);

/** Prefixes our transcript writers use for the AI counterpart. */
export const NON_REP_SPEAKER_LABELS = new Set([
  "customer",
  "client",
  "buyer",
  "prospect",
  "persona",
  "agent",
  "assistant",
  "bot",
]);

export const MAX_LABEL_WORDS = 5;

// A speaker label is a name, not a sentence: it opens with a capital, may carry
// digits ("Agent 47"), an ampersand ("Acme & Co"), parentheses or a dot, and may
// hold lowercase particles in the middle ("Jordan van der Berg"). Requiring the
// LAST word to be capitalised (or numeric) is what keeps ordinary prose such as
// "...confirm that the address is:" from opening a false speaker boundary.
//
// Unicode-aware, not `[A-Za-z]`: personas are named by studios in whatever
// language they sell in, and an ASCII-only rule silently drops "Élodie" and
// "李雷" out of the transcript entirely. `\p{Lo}` is what covers scripts with no
// case distinction at all, where "opens with a capital" has no meaning.
const FIRST_NAME_WORD = /^[\p{Lu}\p{Lo}][\p{L}\p{N}.'’&()/-]*$/u;
const NAME_WORD = /^[\p{Lu}\p{Lo}\p{N}(][\p{L}\p{N}.'’&()/-]*$/u;
const PARTICLE_WORD = /^(?:\p{Ll}[\p{Ll}'’-]{0,4}|&|\+|\/)$/u;

/**
 * Comparison key for a speaker label. Punctuation and case are dropped so
 * "Rucker-Burton" and "rucker burton" are the same person, and so two personas
 * that differ only in case never read as two speakers.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function speakerKey(value) {
  if (typeof value !== "string") return "";
  // Letters and numbers in ANY script survive: `[^a-z0-9]` would reduce "Élodie"
  // to "lodie" and "李雷" to the empty string, at which point the transcript has
  // no way to tell that persona's turns from anyone else's.
  return value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/**
 * True when two labels name the same person. A whole-word prefix counts, because
 * one surface may label a turn "Sam" while another has the full "Sam Torres".
 *
 * @param {unknown} a
 * @param {unknown} b
 * @returns {boolean}
 */
export function sameSpeakerName(a, b) {
  const left = speakerKey(a);
  const right = speakerKey(b);
  if (!left || !right) return false;
  if (left === right) return true;
  return left.startsWith(`${right} `) || right.startsWith(`${left} `);
}

/**
 * One of the role labels our own transcript writers use, such as "Customer".
 *
 * @param {unknown} label
 * @returns {boolean}
 */
export function isRoleLabel(label) {
  const key = speakerKey(label);
  return REP_SPEAKER_LABELS.has(key) || NON_REP_SPEAKER_LABELS.has(key);
}

/**
 * Does this label look like somebody's name rather than the middle of a sentence?
 *
 * @param {unknown} label
 * @param {Object} [options]
 * @param {number} [options.minWords]
 * @param {number} [options.maxWords]
 * @param {number} [options.maxLength]
 * @param {Array<string>} [options.knownSpeakers] - Labels accepted whatever their
 *   shape, because the caller already knows who is in the room.
 * @returns {boolean}
 */
export function looksLikeSpeakerName(label, options = {}) {
  const {
    minWords = 1,
    maxWords = MAX_LABEL_WORDS,
    maxLength = 50,
    knownSpeakers = [],
  } = options;

  if (typeof label !== "string") return false;
  const trimmed = label.trim();
  if (!trimmed) return false;

  if (knownSpeakers.some((name) => sameSpeakerName(trimmed, name))) return true;
  if (trimmed.length > maxLength) return false;

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < minWords || words.length > maxWords) return false;
  if (!FIRST_NAME_WORD.test(words[0])) return false;
  if (!NAME_WORD.test(words[words.length - 1])) return false;

  return words
    .slice(1, -1)
    .every((word) => NAME_WORD.test(word) || PARTICLE_WORD.test(word));
}
